import React, { useState } from 'react';

import {
  getCrmIntegrationLoginUrl,
  getCrmIntegrationLoginUrlFromEvi2ken,
} from '~/api/crms';
import { User } from '~/components/Shared/User/User';
import { Accordion } from '~/eds/src/components/Accordion/Accordion';
import { Button } from '~/eds/src/components/Button/Button';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Link } from '~/eds/src/components/Link/Link';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { Table } from '~/eds/src/modules/Table/Table';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useAsync } from '~/hooks/useAsync';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { useRouting } from '~/routing/useRouting';

function SalesIntegrations() {
  const intl = useIntl();
  const { params } = useRouting();
  const { clientId } = params;
  const { toast } = useToast();

  const [syncPairToDelete, setSyncPairToDelete] = useState(null);

  const { data: syncPairs = [], isLoading: isLoadingSyncPairs } =
    api.endpoints.getCrmConnections.useQuery({ clientId }, { skip: !clientId });

  const [deleteCRMIntegration, { isLoading: isDeletingIntegration }] =
    api.endpoints.deleteCRMIntegration.useMutation();

  const deleteConfirmationText = intl.formatMessage(
    {
      id: 'SalesIntegrations.deleteConfirmationText',
      defaultMessage:
        'Are you sure you want to delete connection to Salesforce Org ID <strong>{extSystemId}</strong>? Deleting the connection will not stop an active file transfer, and will not delete any files that have been transferred. <strong>This action cannot be reverted.</strong>',
      description:
        'Confirmation text for deleting Salesforce connection. Example: extSystemId = "00D5j000000X8BPEA0"',
    },
    { extSystemId: syncPairToDelete?.extSystemId },
  );

  const isUsingEvi2ken = useFlag(FlagType.MultipleSfdcMigrationToEvi2ken);

  const onClickDelete = async () => {
    deleteCRMIntegration(syncPairToDelete)
      .unwrap()
      .then(() => {
        toast({
          status: 'success',
          message: intl.formatMessage(
            {
              id: 'SalesIntegrations.deleteConnectionSuccess',
              defaultMessage: '{extSystemId} connection has been deleted.',
              description:
                'Success message when connection is deleted. Example: extSystemId = "123"',
            },
            { extSystemId: syncPairToDelete.extSystemId },
          ),
        });
      })
      .catch(() => {
        toast({
          status: 'danger',
          message: intl.formatMessage({
            id: 'SalesIntegrations.deleteConnectionError',
            defaultMessage:
              'Unable to delete Salesforce connection, please try again.',
            description: 'Error message when failing to delete connection',
          }),
        });
      });
  };

  const handleHideDeleteModal = () => {
    setSyncPairToDelete(null);
  };

  const rowActions = [
    {
      label: intl.formatMessage({
        id: 'SalesIntegrations.delete',
        defaultMessage: 'Delete',
        description: 'Row action to delete connection',
      }),
      onClick: (syncpair) => {
        setSyncPairToDelete(syncpair);
      },
    },
  ];

  const columns = [
    {
      key: 'extSystemId',
      title: intl.formatMessage({
        id: 'SalesIntegrations.orgId',
        defaultMessage: 'Org ID',
        description: 'Column header for Salesforce Org ID',
      }),
      cellType: 'text',
      mapCellProps: ({ extSystemId }) => ({
        text: extSystemId,
      }),
    },
    {
      key: 'link',
      title: intl.formatMessage({
        id: 'SalesIntegrations.link',
        defaultMessage: 'Link',
        description: 'Column header for connection link',
      }),
      cellType: 'link',
      minWidth: 'l',
      renderCell: ({ authPath }) => {
        return (
          <Link href={`${authPath}`} shouldTruncate={true}>
            {authPath}
          </Link>
        );
      },
    },
    {
      key: 'extUserId',
      title: intl.formatMessage({
        id: 'SalesIntegrations.createdBy',
        defaultMessage: 'Created By',
        description: 'Column header for user who created the connection',
      }),
      cellType: 'user',
      mapCellProps: ({ extUserId }) => ({
        asyncUser: {
          id: extUserId,
          render: User,
        },
        mode: 'avatar-name',
      }),
    },
    {
      key: 'createdDate',
      title: intl.formatMessage({
        id: 'SalesIntegrations.createdDate',
        defaultMessage: 'Created date',
        description: 'Column header for creation date',
      }),
      cellType: 'datetime',
      mapCellProps: ({ createdDate }) => ({
        datetime: createdDate,
      }),
    },
    {
      key: 'status',
      cellType: 'chips',
      title: intl.formatMessage({
        id: 'SalesIntegrations.status',
        defaultMessage: 'Status',
        description: 'Column header for connection status',
      }),
      mapCellProps: ({ error }) => {
        const text = error
          ? intl.formatMessage({
              id: 'SalesIntegrations.connectionError',
              defaultMessage: 'Connection error',
              description: 'Status message for connection error',
            })
          : intl.formatMessage({
              id: 'SalesIntegrations.connected',
              defaultMessage: 'Connected',
              description: 'Status message for connected state',
            });
        const status = error ? 'danger' : 'new';
        const statusChip = {
          text,
          status,
        };
        return {
          chips: [statusChip],
        };
      },
    },
  ];

  const loadingContent = {
    isLoading: isLoadingSyncPairs || isDeletingIntegration,
  };

  const placeholderContent =
    syncPairs.length === 0
      ? {
          message: intl.formatMessage({
            id: 'SalesIntegrations.noConnections',
            defaultMessage: 'No connections',
            description: 'Message displayed when there are no connections',
          }),
        }
      : undefined;

  const { executor: fetchIntegrationLoginUrl } = useAsync(
    isUsingEvi2ken
      ? getCrmIntegrationLoginUrlFromEvi2ken
      : getCrmIntegrationLoginUrl,
    {
      integrationUrl: window.location.href,
    },
    {
      errorToastMessage: intl.formatMessage({
        id: 'SalesIntegrations.fetchLoginUrlError',
        defaultMessage:
          'Unable to fetch Salesforce login url, please try again.',
        description: 'Error message when failing to fetch login URL',
      }),
      successHandler: (redirectResponse) => {
        const { redirectUrl } = redirectResponse;
        window.location.href = redirectUrl;
        toast({
          status: 'success',
          message: intl.formatMessage({
            id: 'SalesIntegrations.connectionCreatedSuccess',
            defaultMessage: 'New Salesforce connection has been created.',
            description: 'Success message when new connection is created',
          }),
        });
      },
    },
  );

  const items = [
    {
      content: (
        <Layout direction="column" spacing={6}>
          <Layout align="left" p={4} justify="space-between">
            <Text preset="description">
              {intl.formatMessage({
                id: 'SalesIntegrations.description',
                defaultMessage:
                  'Generate an Evisort API Key to use in your Salesforce configuration.',
                description: 'Description for Salesforce integration setup',
              })}
              {` `}
              <Link
                pathname={`/admin/console/${params.clientId}/integrations`}
                search="?tab=api-management"
              >
                {intl.formatMessage({
                  id: 'SalesIntegrations.goToApiManagement',
                  defaultMessage: 'Go to Evisort API Management.',
                  description: 'Link text to API management page',
                })}
              </Link>
            </Text>
            <Button
              text={intl.formatMessage({
                id: 'SalesIntegrations.connectSalesforceAccount',
                defaultMessage: 'Connect a Salesforce Account',
                description: 'Button text to connect Salesforce account',
              })}
              variant="primary"
              onClick={fetchIntegrationLoginUrl}
            />
          </Layout>
          {
            <ContentContainer
              loadingContent={loadingContent}
              placeholderContent={placeholderContent}
            >
              <Table
                name="storage-integrations"
                columns={columns}
                data={syncPairs}
                options={{
                  enableManageColumns: false,
                  enableExportXlsx: false,
                  enableSelectRows: false,
                }}
                rowActions={rowActions}
                isLoading={isLoadingSyncPairs}
                totalCount={syncPairs.length}
              />
            </ContentContainer>
          }
          <Modal
            enableHideOnPrimaryAction={true}
            isVisible={syncPairToDelete}
            onHide={handleHideDeleteModal}
            primaryAction={{
              variant: 'danger',
              text: intl.formatMessage({
                id: 'SalesIntegrations.deleteButton',
                defaultMessage: 'Delete',
                description: 'Delete button text in confirmation modal',
              }),
              onClick: onClickDelete,
            }}
            title={intl.formatMessage({
              id: 'SalesIntegrations.deleteConnectionTitle',
              defaultMessage: 'Delete Salesforce connection?',
              description: 'Title for delete connection modal',
            })}
          >
            <Markdown text={deleteConfirmationText} />
          </Modal>
        </Layout>
      ),
      title: intl.formatMessage({
        id: 'SalesIntegrations.accordionTitle',
        defaultMessage: 'Evisort Contract Management for Salesforce',
        description: 'Title for Salesforce integration accordion',
      }),
      isExpanded: true,
    },
  ];

  return <Accordion items={items} />;
}

export default SalesIntegrations;
